import {
  ChevronDownIcon,
  ChevronRightIcon,
  Tag01Icon,
  Tag03Icon,
} from '@langchain/untitled-ui-icons';
import ListItemButton from '@mui/joy/ListItemButton';

import { MouseEventHandler, ReactNode, RefObject, useEffect } from 'react';
import { VirtuosoHandle } from 'react-virtuoso';

import { HubRunTag } from '@/components/HubRunTag';
import { RunLatencyChip } from '@/components/RunLatencyChip';
import { RunStatusChip } from '@/components/RunStatusChip';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { IconButton, Text, Tooltip } from '@/design-system';
import { RunSchema } from '@/types/schema';
import { getModelNameFromRun } from '@/utils/get-model-name-from-run';
import { getRunName } from '@/utils/get-run-name';
import { getHubInfoFromRun } from '@/utils/hub';
import { cn } from '@/utils/tailwind';
import { utcTime } from '@/utils/utc-time';

import { RowDepthMeta } from '../utils/utils';
import { calculateLatency } from './RunStatsCard';
import { RunType } from './RunType';
import { TokensChip } from './TokensChip';
import { useNodeAppearanceSettings } from './useNodeAppearanceStore';

function FoldToggle(props: {
  folded: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) {
  const title = props.folded ? 'Expand' : 'Collapse';
  return (
    <IconButton
      data-testid="fold-toggle"
      icon={props.folded ? ChevronRightIcon : ChevronDownIcon}
      label={title}
      tooltipProps={{
        side: 'right',
      }}
      className="border-none bg-transparent p-0.5"
      title={title}
      onClick={props.onClick}
      variant="plain"
      color="secondary"
    />
  );
}

function RunTreeNodeLines(props: {
  depth?: number;
  depthMeta?: RowDepthMeta;
  children: ReactNode;
  isFlatStructure?: boolean;
}) {
  if (props.depth == null || props.depthMeta == null || props.isFlatStructure)
    return <div style={{ paddingTop: 'var(--tree-py)' }}>{props.children}</div>;

  const padding: ReactNode[] = [];
  for (let i = 0; i < props.depth; i += 1) {
    const col: RowDepthMeta[number] = props.depthMeta[i] ?? {};

    padding.push(
      <div className="relative h-auto w-5 flex-shrink-0" key={i}>
        {col.right && (
          <div
            className={cn(
              'absolute left-1/2 top-0 -ml-[0.5px] w-1/2 rounded-bl-lg border-b border-l border-current',
              col.right === 'black' && 'text-[var(--gray-200)]',
              col.right === 'red' && 'text-error',
              col.right === 'blue' && 'z-[1] text-brand-green-400',
              col.right === 'purple' && 'z-[1] text-purple'
            )}
            style={{ height: 'calc(10px + var(--tree-py))' }}
          />
        )}

        {col.bottom && (
          <div
            className={cn(
              'absolute inset-y-0 left-1/2 -ml-[0.5px] w-px bg-current',
              col.bottom === 'black' && 'text-[var(--gray-200)]',
              col.bottom === 'red' && 'text-error',
              col.bottom === 'blue' && 'text-brand-green-400',
              col.bottom === 'purple' && 'text-purple'
            )}
          />
        )}
      </div>
    );
  }
  return (
    <>
      {padding}
      <div
        className="flex flex-col items-center"
        style={{ paddingTop: 'var(--tree-py)' }}
      >
        {props.children}
        {props.depthMeta[props.depth]?.bottom && (
          <div
            className={cn(
              'w-px flex-grow bg-current',
              props.depthMeta[props.depth].bottom === 'black' &&
                'text-[var(--gray-200)]',
              props.depthMeta[props.depth].bottom === 'red' && 'text-error',
              props.depthMeta[props.depth].bottom === 'blue' &&
                'text-brand-green-400',
              props.depthMeta[props.depth].bottom === 'purple' && 'text-purple'
            )}
          />
        )}
      </div>
    </>
  );
}

const RootRunInfo = ({
  run,
  isFlatStructure,
  conversationIdx,
  rootRunFormat = 'trace',
}) => {
  if (!isFlatStructure) {
    if (rootRunFormat === 'thread') {
      return (
        <>
          {conversationIdx !== undefined && (
            <Text variant="sm" className="whitespace-nowrap" weight="medium">
              Turn {conversationIdx + 1}
            </Text>
          )}
          <RunStatusChip run={run} size="xs" showText={false} />
        </>
      );
    }
    return (
      <>
        <RunStatusChip run={run} size="xs" showText={false} />
        {conversationIdx !== undefined && (
          <Text variant="sm" className="whitespace-nowrap text-tertiary">
            Turn {conversationIdx + 1}
          </Text>
        )}
      </>
    );
  }

  return conversationIdx !== undefined ? (
    <Text variant="sm" className="whitespace-nowrap text-tertiary">
      Turn {conversationIdx + 1}
    </Text>
  ) : (
    <RunStatusChip run={run} size="xs" showText={false} />
  );
};

// Component for displaying tags in the trace tree - shows only 1 tag with icon
function TraceTreeTags({ run }: { run: RunSchema }) {
  const HIDDEN = ['langsmith:hidden'];
  const tags = run?.tags?.filter?.((t) => !HIDDEN.includes(t));

  // More explicit check to ensure we don't render when there are no tags
  if (!tags || tags.length === 0) return null;

  // Show only the first tag with Tag03Icon
  const firstTag = tags[0];

  // Create tooltip content showing all tags
  const tooltipContent = (
    <div className="rounded px-2 py-2 text-xs">
      <div className="mb-1 grid grid-cols-[auto,1fr] gap-4">
        <div className="font-bold">Tags</div>
        <div className="text-right font-normal">{tags.length}</div>
      </div>
      {tags.map((tag, index) => (
        <ul key={index} className="mb-1 ml-1 grid grid-cols-[auto,1fr] gap-4">
          <li>{tag}</li>
        </ul>
      ))}
    </div>
  );

  return (
    <Tooltip title={tags.length === 1 ? undefined : tooltipContent}>
      <div className="flex items-center gap-0.5">
        <div
          className={cn(
            'flex items-center gap-1 whitespace-nowrap rounded-md border border-secondary bg-primary px-1 py-0.5 text-xs',
            tags.length > 1 &&
              'from-tertiary to-primary/80 bg-gradient-to-r shadow-sm'
          )}
        >
          {tags.length === 1 ? (
            <Tag01Icon className="h-3 w-3 text-tertiary" />
          ) : (
            <Tag03Icon className="h-3 w-3 text-tertiary" />
          )}
          {firstTag}
        </div>
      </div>
    </Tooltip>
  );
}

export function RunTreeNode(props: {
  run: RunSchema;
  rootRun: RunSchema;
  activeRunId?: string;
  isPublicPage: boolean;
  isActiveDescendant: boolean;
  onClick: (run: RunSchema) => void;
  scrollIdx: number;
  scrollRef?: RefObject<VirtuosoHandle>;
  hasChildren?: boolean;
  hasChildrenVisible?: boolean;
  isFlatStructure?: boolean;
  setFoldedRuns: React.Dispatch<React.SetStateAction<string[]>>;
  depth?: number;
  depthMeta?: RowDepthMeta;
  focused?: boolean;
  showRootExpandToggle?: boolean;
  conversationIdx?: number;
  onMouseEnter?: (run: RunSchema) => void;
  onMouseLeave?: () => void;
  hideTitles?: boolean;
  rootRunFormat?: 'trace' | 'thread';
}) {
  const { hideTitles } = props;
  const { showLatency, showTokens, showTags } = useNodeAppearanceSettings(
    props.isPublicPage
  );
  const isCurrentRun = props.activeRunId === props.run.id;
  const isRootRun = props.run.id === props.rootRun?.id;
  const hubInfo = getHubInfoFromRun(props.run);

  useEffect(() => {
    if (isCurrentRun) {
      props.scrollRef?.current?.scrollToIndex({
        index: props.scrollIdx,
        align: 'center',
        behavior: 'smooth',
      });
    }
  }, [isCurrentRun, props.scrollIdx, props.scrollRef]);

  const model = getModelNameFromRun(props.run);

  const hasModelNameTag = !!model && props.run.run_type === 'llm';
  const showExpandToggle =
    !hideTitles && (isRootRun ? props.showRootExpandToggle : props.hasChildren);

  const hasTokens =
    props.run.run_type === 'llm' && (props.run.total_tokens ?? 0) > 0;
  const hasTags = (props.run.tags?.length ?? 0) > 0;
  const showOnSecondLine =
    showLatency &&
    ((showTokens && hasTokens) || (showTags && hasTags)) &&
    !hideTitles;

  const timeToFirstTokenMs =
    props.run.first_token_time != null
      ? utcTime(props.run.first_token_time).diff(
          utcTime(props.run.start_time),
          'milliseconds'
        )
      : null;

  const latencyAndTokens = (
    <div className="flex items-center gap-1">
      {(showLatency || hideTitles) &&
        props.run?.start_time &&
        props.run?.end_time &&
        !isRootRun && (
          <Tooltip
            title={
              timeToFirstTokenMs != null && (
                <div className="font-bold">
                  Time to first token:{' '}
                  <span className="font-normal">{timeToFirstTokenMs}ms</span>
                </div>
              )
            }
          >
            <span className="text-xs text-tertiary">
              {
                calculateLatency(props.run.start_time, props.run.end_time)
                  .latency
              }
            </span>
          </Tooltip>
        )}

      {showTokens &&
        !isRootRun &&
        !hideTitles &&
        props.run.run_type === 'llm' &&
        (props.run.total_tokens ?? 0) > 0 && <TokensChip run={props.run} />}

      {showTags &&
        (props.run.tags?.length ?? 0) > 0 &&
        !isRootRun &&
        !hideTitles &&
        !props.isFlatStructure && <TraceTreeTags run={props.run} />}
    </div>
  );
  const hideTitleV2Root = props.rootRunFormat === 'thread' && isRootRun;

  return (
    <ListItemButton
      id={`run-tree-node-${props.run.id}`}
      variant="plain"
      onClick={() => props.onClick(props.run)}
      className="transition-colors"
      data-testid={`run-tree-node-${getRunName(props.run)}`}
      sx={{
        '--tree-py': '6px',
        px: '14px',
        display: 'flex',
        alignItems: 'stretch',
        borderLeft: '2px solid transparent',
        '&:hover': {
          backgroundColor: 'var(--bg-primary_hover)',
        },
        ...(isCurrentRun
          ? {
              backgroundColor: 'var(--bg-brand-secondary)',
              borderColor: 'var(--bg-brand-primary)',
              '&:hover': {
                backgroundColor: 'var(--bg-brand-secondary)',
              },
            }
          : null),
        ...(props.isActiveDescendant && !isCurrentRun
          ? {
              backgroundColor: 'var(--bg-brand-tertiary)',
              '&:hover': {
                backgroundColor: 'var(--bg-brand-secondary)',
              },
            }
          : null),
      }}
      onMouseEnter={() => props.onMouseEnter?.(props.run)}
      onMouseLeave={props.onMouseLeave}
    >
      <RunTreeNodeLines
        depth={props.depth}
        depthMeta={props.depthMeta}
        isFlatStructure={props.isFlatStructure}
      >
        <RunType
          className={cn(
            props.focused &&
              'outline outline-1 outline-offset-1 outline-[#FEDF89] dark:outline-[#FEC84B]'
          )}
          run={props.run}
          rootRunFormat={props.rootRunFormat}
          isCurrentRun={isCurrentRun}
        />
      </RunTreeNodeLines>
      <div className="my-auto ml-2 grid w-full grid-cols-[1fr,auto] items-center py-[var(--tree-py)]">
        <div className="flex min-w-0 flex-col justify-center gap-2">
          <div className="flex min-h-5 min-w-0 items-center gap-2">
            {isRootRun && props.rootRunFormat === 'thread' && (
              <RootRunInfo
                run={props.run}
                isFlatStructure={props.isFlatStructure}
                conversationIdx={props.conversationIdx}
                rootRunFormat={props.rootRunFormat}
              />
            )}
            {!hideTitles && !hideTitleV2Root && (
              <>
                {(!props.isFlatStructure || !hasModelNameTag) && (
                  <TextOverflowTooltip
                    textVariant="md"
                    textProps={{
                      weight: 'medium',
                    }}
                    className={cn(
                      props.focused &&
                        'bg-[#FEDF89] py-[3px] text-black outline outline-1 outline-[#FEDF89] dark:bg-[#FEC84B] dark:outline-[#FEC84B]'
                    )}
                  >
                    {getRunName(props.run)}
                  </TextOverflowTooltip>
                )}
                {hasModelNameTag && typeof model === 'string' && (
                  <TextOverflowTooltip
                    textVariant="sm"
                    className="mt-[-2px] rounded-md border border-secondary p-1"
                  >
                    {model}
                  </TextOverflowTooltip>
                )}
              </>
            )}

            {hubInfo && <HubRunTag hubInfo={hubInfo} />}

            {!showOnSecondLine && latencyAndTokens}

            {isRootRun && props.rootRunFormat !== 'thread' && (
              <RootRunInfo
                run={props.run}
                isFlatStructure={props.isFlatStructure}
                conversationIdx={props.conversationIdx}
                rootRunFormat={props.rootRunFormat}
              />
            )}

            {showExpandToggle && (
              <div className="flex flex-1 justify-end">
                <FoldToggle
                  folded={!props.hasChildrenVisible}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.setFoldedRuns((prev) => {
                      if (prev.includes(props.run.id)) {
                        return prev.filter((id) => id !== props.run.id);
                      } else if (props.hasChildrenVisible) {
                        // Only fold the run if it has children visible
                        // in conversation view, root runs don't have children visible
                        // by default until children are loaded. So don't fold them
                        // on initial click when they're on in the folded list
                        return [...prev, props.run.id];
                      } else {
                        return prev;
                      }
                    });
                    // open the run if it's not selected
                    if (
                      !props.hasChildrenVisible &&
                      props.activeRunId !== props.run.id
                    ) {
                      props.onClick(props.run);
                    }
                  }}
                />
              </div>
            )}
          </div>
          {showOnSecondLine && !isRootRun && latencyAndTokens}
          {isRootRun && !props.isFlatStructure && (
            <div className="flex flex-wrap items-center gap-2 pr-2 empty:hidden">
              {showLatency && (
                <Tooltip
                  title={
                    timeToFirstTokenMs != null && (
                      <div className="font-bold">
                        Time to first token:{' '}
                        <span className="font-normal">
                          {timeToFirstTokenMs}ms
                        </span>
                      </div>
                    )
                  }
                >
                  <RunLatencyChip run={props.run} />
                </Tooltip>
              )}

              {showTokens && (props.run.total_tokens ?? 0) > 0 && (
                <TokensChip run={props.run} />
              )}

              {showTags && !props.isPublicPage && (
                <TraceTreeTags run={props.run} />
              )}
            </div>
          )}
        </div>
      </div>
    </ListItemButton>
  );
}
